import React, { useCallback, useEffect, useState } from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, Search, AlertTriangle } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const STATUS_LABELS = { pendiente_pago: "Pendiente", confirmado: "Confirmado", completado: "Completado", cancelado: "Cancelado" };
const tabs = ["Todas", "pendiente_pago", "confirmado", "completado", "cancelado"];

const AdminReservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Todas");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (estado) => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/reservas", { params: estado && estado !== "Todas" ? { estado } : {} });
      setReservations(data);
    } catch {
      setError("No se pudieron cargar las reservas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const filtered = reservations.filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return r.id.toLowerCase().includes(q) || r.contact_name.toLowerCase().includes(q) || (r.flight_number || "").toLowerCase().includes(q);
  });

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-reservations">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Operaciones</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Gestión de reservas</h1>

        {error && <p className="mt-4 text-xs text-red-400 font-body">{error}</p>}

        <div className="mt-6 bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#2E2F33] flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-3.5 py-2.5">
              <Search size={14} className="text-[#C9A227]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por ID, cliente o vuelo..."
                className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
                data-testid="reservations-search"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {tabs.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 border rounded-full text-[10px] uppercase tracking-wider font-display transition ${tab === t ? "bg-[#C9A227] border-[#C9A227] text-[#0D0E10] font-bold" : "border-[#2E2F33] text-[#A0A0A5] hover:border-[#C9A227]/50"}`}
                  data-testid={`admin-filter-${t}`}
                >
                  {t === "Todas" ? t : STATUS_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reservations-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display border-b border-[#2E2F33]">
                  <th className="text-left px-5 py-3 font-normal">ID</th>
                  <th className="text-left px-5 py-3 font-normal">Cliente</th>
                  <th className="text-left px-5 py-3 font-normal">Modalidad</th>
                  <th className="text-left px-5 py-3 font-normal">Fecha · Hora</th>
                  <th className="text-left px-5 py-3 font-normal">Vuelo</th>
                  <th className="text-left px-5 py-3 font-normal">Pago</th>
                  <th className="text-left px-5 py-3 font-normal">Total</th>
                  <th className="text-left px-5 py-3 font-normal">Estado</th>
                </tr>
              </thead>
              <tbody>
                {!loading &&
                  filtered.map((r) => {
                    const needsReview = !r.driver_is_active && (r.status === "pendiente_pago" || r.status === "confirmado");
                    return (
                      <tr key={r.id} className="border-b border-[#2E2F33] hover:bg-[#0D0E10]/50 transition" data-testid={`res-row-${r.id}`}>
                        <td className="px-5 py-4 font-mono text-xs text-[#C9A227]">{r.id.slice(0, 8).toUpperCase()}</td>
                        <td className="px-5 py-4 font-body">{r.contact_name}</td>
                        <td className="px-5 py-4 font-body text-[#A0A0A5] capitalize">{r.modality}</td>
                        <td className="px-5 py-4 font-body text-[#A0A0A5]">{new Date(r.scheduled_at).toLocaleString("es-PE")}</td>
                        <td className="px-5 py-4 font-mono text-xs text-[#A0A0A5]">{r.flight_number || "—"}</td>
                        <td className="px-5 py-4 font-body text-[#A0A0A5]">
                          {r.payment_method} · {r.payment_status === "pagado" ? "Pagado" : "Pendiente"}
                        </td>
                        <td className="px-5 py-4 font-display font-bold text-[#C9A227]">S/ {r.price_total}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={STATUS_LABELS[r.status]} />
                            {needsReview && (
                              <span
                                className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300"
                                title="El conductor asignado está desactivado -- requiere revisión manual (reasignar o cancelar)"
                                data-testid={`res-driver-inactive-${r.id}`}
                              >
                                <AlertTriangle size={12} /> Conductor inactivo
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">
                      No hay reservas que coincidan.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

export default AdminReservations;
